// @/components/animation/tokens.ts
//
// Single source of truth for the site's motion feel. Tune a value here
// to change how the WHOLE site animates — no component edits needed.
//
// Boundary: these tokens drive content reveals + page transitions via
// `motion` (Framer Motion). The two logo components (LogoMarquee,
// LogoCloudVertical) run on gsap and intentionally do NOT use these —
// see animation/README.md.
//
// Folded in from the old springs.js (2026-05-21). Only the springs we
// actually use survive — the unused springDefault/Panel/Quick + the
// duplicate stagger constant were pruned.

import type { Transition } from 'motion/react'

// ── Spring physics ───────────────────────────────────────────
export const springs = {
  // ── THE reveal spring — SINGLE SOURCE OF TRUTH ──────────────
  // Every scroll-reveal on the site uses this: <Reveal>,
  // <AnimateOnView>, and <StaggerItem>. Edit this one object to retune
  // the whole site's reveal feel. Pure physics — NO `bounce` (that's
  // the duration-based spring API and conflicts with stiffness/damping/
  // mass, which is what made reveals behave inconsistently).
  reveal: { type: 'spring', damping: 22, stiffness: 220, mass: 0.5 },
  // Legacy springs — kept only for the cookie-consent toast.
  gentle: { type: 'spring', damping: 30, stiffness: 200, mass: 0.8 },
  pop: { type: 'spring', damping: 22, stiffness: 320, mass: 0.6 },
} as const satisfies Record<string, Transition>

// ── Durations (seconds) ──────────────────────────────────────
export const durations = {
  // Opacity ramp layered on top of the spring on every reveal — and the
  // route-to-route crossfade (wrapPageElement), which reuses `revealFade`
  // so the whole site shares one opacity feel.
  fade: 0.35,
} as const

// Opacity fade for reveals. MUST be a tween: a per-value `opacity`
// transition INHERITS the parent spring's `type`, so without forcing
// `type: 'tween'` the spring physics drive opacity and `duration` is
// silently ignored (the "opacity is ignored" bug). Pair this with
// springs.reveal on every reveal: `{ ...springs.reveal, opacity: revealFade }`.
export const revealFade = { type: 'tween', duration: durations.fade, ease: 'easeOut' } as const

// ── Easings ──────────────────────────────────────────────────
export const eases = {
  inOut: 'easeInOut',
} as const

// ── Stagger cadence ──────────────────────────────────────────
export const stagger = {
  // Delay between cascading children in a StaggerGroup (seconds).
  step: 0.12,
} as const

// ── Shared viewport trigger for scroll reveals ───────────────
// once: a section animates only the first time it enters view.
// margin: fire slightly before full intersection so it feels
// anticipatory rather than late.
// Shared scroll trigger for ALL reveals (AnimateOnView, StaggerGroup) —
// matches <Reveal>'s own VIEWPORT so everything fires at the same point.
export const revealViewport = { once: true, margin: '-12% 0px' } as const
